/**
 * Smart Filters v2
 * Complete UI/UX redesign with mobile drawer and active filters bar
 */

class SmartFiltersForm extends HTMLElement {
  constructor() {
    super();

    // State
    this.activeFilters = new Set();

    // Cache DOM elements
    this.cacheElements();

    // Bind events
    this.bindEvents();

    // Initialize
    this.init();
  }

  cacheElements() {
    // All filter chips (both desktop and drawer)
    this.allChips = this.querySelectorAll('.smart-filter__chip');

    // Desktop elements
    this.desktopChips = this.querySelectorAll('.smart-filters__desktop .smart-filter__chip');

    // Active bar is now in product grid section (document scope)
    this.activeBar = document.querySelector('[data-active-bar]');
    this.activeChipsContainer = document.querySelector('[data-active-chips]');

    // Mobile elements
    this.drawer = this.querySelector('[data-drawer]');
    this.drawerChips = this.querySelectorAll('.smart-filters__drawer .smart-filter__chip');
    this.openDrawerBtn = this.querySelector('[data-open-drawer]');
    this.closeDrawerBtns = this.querySelectorAll('[data-close-drawer]');
    this.applyDrawerBtn = this.querySelector('[data-apply-drawer]');
    this.drawerGroupToggles = this.querySelectorAll('[data-toggle-group]');
    this.mobileActiveCount = this.querySelector('[data-active-count]');
    this.drawerCount = this.querySelector('[data-drawer-count]');

    // Shared elements (search document for clear buttons - one in active bar, one in drawer)
    this.clearBtns = document.querySelectorAll('[data-clear-filters]');
    this.resultCountEls = document.querySelectorAll('[data-result-count]');

    // Sort dropdown
    this.sortSelect = this.querySelector('[data-sort-select]');
  }

  bindEvents() {
    // Chip clicks (desktop)
    this.desktopChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.onChipClick(e, 'desktop'));
    });

    // Chip clicks (drawer) - these don't auto-apply
    this.drawerChips.forEach(chip => {
      chip.addEventListener('click', (e) => this.onChipClick(e, 'drawer'));
    });

    // Clear buttons
    this.clearBtns.forEach(btn => {
      btn.addEventListener('click', () => this.clearAllFilters());
    });

    // Mobile drawer
    if (this.openDrawerBtn) {
      this.openDrawerBtn.addEventListener('click', () => this.openDrawer());
    }

    this.closeDrawerBtns.forEach(btn => {
      btn.addEventListener('click', () => this.closeDrawer());
    });

    if (this.applyDrawerBtn) {
      this.applyDrawerBtn.addEventListener('click', () => this.applyDrawerAndClose());
    }

    // Drawer group toggles (accordion)
    this.drawerGroupToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => this.toggleDrawerGroup(e));
    });

    // Escape key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('smart-filters__drawer--open')) {
        this.closeDrawer();
      }
    });

    // Sort dropdown change
    if (this.sortSelect) {
      this.sortSelect.addEventListener('change', () => this.onSortChange());
    }
  }

  init() {
    // Sort filter chips alphabetically (handles accents)
    this.sortFilterChips();

    // Create two-column layout wrapper on desktop
    this.createLayoutWrapper();

    // Calculate static counts once (never changes)
    this.setStaticChipCounts();

    // Restore state from URL
    this.restoreFromURL();

    // Update cross-group availability based on restored filters
    this.updateCrossGroupAvailability();

    // Sync drawer chips with active state
    this.syncDrawerChips();

    // Update UI
    this.updateActiveBar();
    this.updateMobileCount();
    this.updateResultCount();
  }

  sortFilterChips() {
    // Sort chips within each container alphabetically (locale-aware for accents)
    const chipContainers = this.querySelectorAll('.smart-filters__chips, .smart-filters__drawer-chips');

    chipContainers.forEach(container => {
      const chips = Array.from(container.querySelectorAll('.smart-filter__chip'));

      chips.sort((a, b) => {
        const labelA = a.querySelector('.smart-filter__label')?.textContent || '';
        const labelB = b.querySelector('.smart-filter__label')?.textContent || '';
        return labelA.localeCompare(labelB, 'fr', { sensitivity: 'base' });
      });

      // Re-append in sorted order
      chips.forEach(chip => container.appendChild(chip));
    });
  }

  setStaticChipCounts() {
    // Calculate and set static counts once - never changes based on selection
    const productCards = document.querySelectorAll('[data-product-tags]');

    this.allChips.forEach(chip => {
      const filterValue = chip.dataset.filterValue;

      // Count products that have this tag
      let count = 0;
      productCards.forEach(card => {
        const productTags = card.dataset.productTags.split(',').map(t => t.trim()).filter(t => t);
        if (productTags.includes(filterValue)) {
          count++;
        }
      });

      // Set the count
      const countEl = chip.querySelector('.smart-filter__count');
      if (countEl) {
        countEl.textContent = count;
      }

      // Store static count for reference
      chip.dataset.staticCount = count;

      // Disable if count is 0
      if (count === 0) {
        chip.classList.add('smart-filter__chip--disabled');
        chip.setAttribute('aria-disabled', 'true');
        chip.setAttribute('tabindex', '-1');
      }
    });
  }

  updateCrossGroupAvailability() {
    // Dynamically disable chips in other groups if they'd produce 0 results
    const productCards = document.querySelectorAll('[data-product-tags]');

    // Group active filters by prefix
    const categoryFilters = [...this.activeFilters].filter(f => f.startsWith('category:'));
    const concernFilters = [...this.activeFilters].filter(f => f.startsWith('concern:'));
    const typeFilters = [...this.activeFilters].filter(f => f.startsWith('type:'));

    // Pre-compute product tags for performance
    const productsData = [...productCards].map(card => ({
      tags: card.dataset.productTags.split(',').map(t => t.trim()).filter(t => t)
    }));

    this.allChips.forEach(chip => {
      const filterValue = chip.dataset.filterValue;
      const staticCount = parseInt(chip.dataset.staticCount || '0', 10);
      const isActive = chip.classList.contains('smart-filter__chip--active');

      // If static count is 0, chip stays disabled regardless
      if (staticCount === 0) {
        return;
      }

      // Active chips are never disabled (user must be able to deselect)
      if (isActive) {
        chip.classList.remove('smart-filter__chip--disabled');
        chip.removeAttribute('aria-disabled');
        chip.removeAttribute('tabindex');
        return;
      }

      // Determine which group this chip belongs to
      const chipGroup = filterValue.split(':')[0];

      // Get active filters from OTHER groups only
      let otherCategoryFilters = categoryFilters;
      let otherConcernFilters = concernFilters;
      let otherTypeFilters = typeFilters;

      if (chipGroup === 'category') {
        otherCategoryFilters = []; // Ignore same group
      } else if (chipGroup === 'concern') {
        otherConcernFilters = []; // Ignore same group
      } else if (chipGroup === 'type') {
        otherTypeFilters = []; // Ignore same group
      }

      // If no filters active in other groups, chip is available
      if (otherCategoryFilters.length === 0 && otherConcernFilters.length === 0 && otherTypeFilters.length === 0) {
        chip.classList.remove('smart-filter__chip--disabled');
        chip.removeAttribute('aria-disabled');
        chip.removeAttribute('tabindex');
        return;
      }

      // Count products that would match if this chip were selected
      let count = 0;
      productsData.forEach(product => {
        const tags = product.tags;

        // Check if product has this chip's tag
        if (!tags.includes(filterValue)) {
          return;
        }

        // Check if product matches filters from other groups (AND logic between groups)
        const matchesCategory = otherCategoryFilters.length === 0 ||
          otherCategoryFilters.some(filter => tags.includes(filter));
        const matchesConcern = otherConcernFilters.length === 0 ||
          otherConcernFilters.some(filter => tags.includes(filter));
        const matchesType = otherTypeFilters.length === 0 ||
          otherTypeFilters.some(filter => tags.includes(filter));

        if (matchesCategory && matchesConcern && matchesType) {
          count++;
        }
      });

      // Enable or disable based on count
      if (count === 0) {
        chip.classList.add('smart-filter__chip--disabled');
        chip.setAttribute('aria-disabled', 'true');
        chip.setAttribute('tabindex', '-1');
      } else {
        chip.classList.remove('smart-filter__chip--disabled');
        chip.removeAttribute('aria-disabled');
        chip.removeAttribute('tabindex');
      }
    });
  }

  createLayoutWrapper() {
    // Only create wrapper on desktop
    if (window.innerWidth < 1024) return;

    const filterSection = this.closest('.collection-smart-filters-section');
    if (!filterSection) return;

    const productGridSection = filterSection.nextElementSibling;
    if (!productGridSection || !productGridSection.classList.contains('section')) return;

    // Check if wrapper already exists
    if (filterSection.parentElement.classList.contains('smart-filters-layout')) return;

    // Create wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'smart-filters-layout';

    // Insert wrapper before filter section
    filterSection.parentNode.insertBefore(wrapper, filterSection);

    // Move both sections into wrapper
    wrapper.appendChild(filterSection);
    wrapper.appendChild(productGridSection);
  }

  // ============================================
  // CHIP INTERACTIONS
  // ============================================

  onChipClick(event, source) {
    const chip = event.currentTarget;

    // Prevent click on disabled chips
    if (chip.classList.contains('smart-filter__chip--disabled')) {
      return;
    }

    const filterValue = chip.dataset.filterValue;
    const isActive = chip.classList.contains('smart-filter__chip--active');

    if (isActive) {
      this.activeFilters.delete(filterValue);
    } else {
      this.activeFilters.add(filterValue);
    }

    // Update chip state
    this.updateChipState(filterValue, !isActive);

    // If desktop, apply immediately
    if (source === 'desktop') {
      this.applyFilters();
    } else {
      // Drawer: just update preview count
      this.updateDrawerPreviewCount();
    }
  }

  updateChipState(filterValue, isActive) {
    // Update all chips with this filter value (both desktop and drawer)
    const chips = this.querySelectorAll(`[data-filter-value="${filterValue}"]`);
    chips.forEach(chip => {
      chip.classList.toggle('smart-filter__chip--active', isActive);
      chip.setAttribute('aria-pressed', isActive.toString());
    });
  }

  syncDrawerChips() {
    // Sync drawer chips with current active filters
    this.drawerChips.forEach(chip => {
      const filterValue = chip.dataset.filterValue;
      const isActive = this.activeFilters.has(filterValue);
      chip.classList.toggle('smart-filter__chip--active', isActive);
      chip.setAttribute('aria-pressed', isActive.toString());
    });
  }

  // ============================================
  // FILTERING LOGIC
  // ============================================

  applyFilters() {
    this.filterProducts();
    this.updateCrossGroupAvailability();
    this.updateActiveBar();
    this.updateMobileCount();
    this.updateResultCount();
    this.updateURL();
  }

  filterProducts() {
    const productCards = document.querySelectorAll('[data-product-tags]');
    const hasActiveFilters = this.activeFilters.size > 0;

    // Group active filters by prefix
    const categoryFilters = [...this.activeFilters].filter(f => f.startsWith('category:'));
    const concernFilters = [...this.activeFilters].filter(f => f.startsWith('concern:'));
    const typeFilters = [...this.activeFilters].filter(f => f.startsWith('type:'));

    productCards.forEach(card => {
      const gridItem = card.closest('li');
      if (!gridItem) return;

      if (!hasActiveFilters) {
        gridItem.classList.remove('smart-filter-hidden');
        return;
      }

      const productTags = card.dataset.productTags.split(',').map(t => t.trim()).filter(t => t);

      // OR within group, AND between groups
      const matchesCategory = categoryFilters.length === 0 ||
        categoryFilters.some(filter => productTags.includes(filter));
      const matchesConcern = concernFilters.length === 0 ||
        concernFilters.some(filter => productTags.includes(filter));
      const matchesType = typeFilters.length === 0 ||
        typeFilters.some(filter => productTags.includes(filter));

      const matchesFilter = matchesCategory && matchesConcern && matchesType;

      if (matchesFilter) {
        gridItem.classList.remove('smart-filter-hidden');
      } else {
        gridItem.classList.add('smart-filter-hidden');
      }
    });
  }

  getVisibleProductCount() {
    const allCards = document.querySelectorAll('[data-product-tags]');
    return [...allCards].filter(card =>
      !card.closest('li')?.classList.contains('smart-filter-hidden')
    ).length;
  }

  // ============================================
  // ACTIVE FILTERS BAR
  // ============================================

  updateActiveBar() {
    if (!this.activeBar || !this.activeChipsContainer) return;

    // Clear current chips
    this.activeChipsContainer.innerHTML = '';

    if (this.activeFilters.size === 0) {
      this.activeBar.classList.remove('smart-filters__active-bar--visible');
      return;
    }

    // Add chips for each active filter
    this.activeFilters.forEach(filterValue => {
      const label = this.getFilterLabel(filterValue);
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'smart-filters__active-chip';
      chip.innerHTML = `
        <span>${label}</span>
        <svg class="smart-filters__active-chip-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
      `;
      chip.addEventListener('click', () => {
        this.activeFilters.delete(filterValue);
        this.updateChipState(filterValue, false);
        this.applyFilters();
      });
      this.activeChipsContainer.appendChild(chip);
    });

    this.activeBar.classList.add('smart-filters__active-bar--visible');
  }

  getFilterLabel(filterValue) {
    // Extract label from filter value (e.g., "category:Vitalité" -> "Vitalité")
    const parts = filterValue.split(':');
    return parts[1] || filterValue;
  }

  // ============================================
  // MOBILE DRAWER
  // ============================================

  openDrawer() {
    if (!this.drawer) return;

    // Sync drawer chips before opening
    this.syncDrawerChips();
    this.updateDrawerPreviewCount();

    this.drawer.classList.add('smart-filters__drawer--open');
    document.body.style.overflow = 'hidden';

    // Focus first chip for accessibility
    const firstChip = this.drawer.querySelector('.smart-filter__chip');
    if (firstChip) {
      setTimeout(() => firstChip.focus(), 100);
    }
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.classList.remove('smart-filters__drawer--open');
    document.body.style.overflow = '';

    // Restore focus to trigger button
    if (this.openDrawerBtn) {
      this.openDrawerBtn.focus();
    }
  }

  applyDrawerAndClose() {
    // Collect active filters from drawer
    this.activeFilters.clear();
    this.drawerChips.forEach(chip => {
      if (chip.classList.contains('smart-filter__chip--active')) {
        this.activeFilters.add(chip.dataset.filterValue);
      }
    });

    // Sync desktop chips
    this.desktopChips.forEach(chip => {
      const filterValue = chip.dataset.filterValue;
      const isActive = this.activeFilters.has(filterValue);
      chip.classList.toggle('smart-filter__chip--active', isActive);
      chip.setAttribute('aria-pressed', isActive.toString());
    });

    // Apply and close
    this.applyFilters();
    this.closeDrawer();
  }

  toggleDrawerGroup(event) {
    const header = event.currentTarget;
    const group = header.closest('.smart-filters__drawer-group');
    if (group) {
      group.classList.toggle('smart-filters__drawer-group--open');
    }
  }

  updateDrawerPreviewCount() {
    if (!this.drawerCount) return;

    // Count what would be visible with drawer selection
    const tempFilters = new Set();
    this.drawerChips.forEach(chip => {
      if (chip.classList.contains('smart-filter__chip--active')) {
        tempFilters.add(chip.dataset.filterValue);
      }
    });

    // Calculate count
    const productCards = document.querySelectorAll('[data-product-tags]');
    const categoryFilters = [...tempFilters].filter(f => f.startsWith('category:'));
    const concernFilters = [...tempFilters].filter(f => f.startsWith('concern:'));
    const typeFilters = [...tempFilters].filter(f => f.startsWith('type:'));

    let count = 0;
    if (tempFilters.size === 0) {
      count = productCards.length;
    } else {
      productCards.forEach(card => {
        const productTags = card.dataset.productTags.split(',').map(t => t.trim()).filter(t => t);

        const matchesCategory = categoryFilters.length === 0 ||
          categoryFilters.some(filter => productTags.includes(filter));
        const matchesConcern = concernFilters.length === 0 ||
          concernFilters.some(filter => productTags.includes(filter));
        const matchesType = typeFilters.length === 0 ||
          typeFilters.some(filter => productTags.includes(filter));

        if (matchesCategory && matchesConcern && matchesType) {
          count++;
        }
      });
    }

    this.drawerCount.textContent = count;
  }

  updateMobileCount() {
    if (!this.mobileActiveCount) return;

    const count = this.activeFilters.size;
    this.mobileActiveCount.textContent = count > 0 ? count : '';
    this.mobileActiveCount.dataset.count = count;
  }

  // ============================================
  // RESULT COUNT
  // ============================================

  updateResultCount() {
    const count = this.getVisibleProductCount();
    const text = `${count} produit${count !== 1 ? 's' : ''}`;

    this.resultCountEls.forEach(el => {
      el.textContent = text;
    });
  }

  // ============================================
  // CLEAR FILTERS
  // ============================================

  clearAllFilters() {
    this.activeFilters.clear();

    // Reset all chips
    this.allChips.forEach(chip => {
      chip.classList.remove('smart-filter__chip--active');
      chip.setAttribute('aria-pressed', 'false');
    });

    // Re-enable chips that were cross-group disabled (but keep static 0-count chips disabled)
    this.updateCrossGroupAvailability();

    this.applyFilters();

    // If drawer is open, update preview
    if (this.drawer?.classList.contains('smart-filters__drawer--open')) {
      this.updateDrawerPreviewCount();
    }
  }

  // ============================================
  // SORTING
  // ============================================

  onSortChange() {
    if (!this.sortSelect) return;

    const sortValue = this.sortSelect.value;
    const url = new URL(window.location);

    url.searchParams.set('sort_by', sortValue);

    // Navigate to sorted URL (Shopify handles sort server-side)
    window.location.href = url.toString();
  }

  // ============================================
  // URL MANAGEMENT
  // ============================================

  updateURL() {
    const url = new URL(window.location);

    // Clear existing filter params
    url.searchParams.delete('filter.p.tag');

    // Add active filters
    this.activeFilters.forEach(tag => {
      url.searchParams.append('filter.p.tag', tag);
    });

    history.replaceState({}, '', url);
  }

  restoreFromURL() {
    const urlParams = new URLSearchParams(window.location.search);

    // Restore filters
    const urlTags = urlParams.getAll('filter.p.tag');
    urlTags.forEach(tag => {
      this.activeFilters.add(tag);
      this.updateChipState(tag, true);
    });

    // Apply if we had filters
    if (urlTags.length > 0) {
      this.filterProducts();
    }
  }
}

// Register custom element
customElements.define('smart-filters-form', SmartFiltersForm);
